// Presigned play-link lifecycle for R2 lessons, extracted from the Vue
// component so the fetch / expiry / single-flight / invalidation logic is unit
// testable with a mocked `$http` (no DOM, no Plyr, no hls.js).
//
// The link is fetched through the profile-new BFF
// (`GET <baseUrl>/api/video/play?lessonId=<id>`) using the app's `$http`, so
// bearer / locale / Session-Id headers and the single-flight refresh-on-401
// interceptor all apply for free. The BFF returns the video-service payload
// verbatim: `{ src, kind:'hls'|'mp4', expiresAt, durationSec, poster, textTracks }`.
// The axios response interceptor runs `camelcaseObjectDeep` on it, which leaves
// those keys untouched (asserted in the tests — see spec §9).

export const EXPIRY_MARGIN_MS = 10000;

/**
 * Static play payload for course-API signed R2 URLs (no BFF /watch refresh).
 * Same surface as {@link createPlaybackLink} so players can share one code path.
 * @param {string} src
 * @param {{kind?: string, expiresAt?: string|null}} [meta]
 */
export function createDirectPlaybackLink(src, meta = {}) {
    const payload = {
        src,
        kind: meta.kind || "mp4",
        expiresAt: meta.expiresAt || null,
        textTracks: meta.textTracks || [],
        poster: meta.poster || null,
    };
    let epoch = 0;

    return {
        get url() {
            return src;
        },
        get payload() {
            return payload;
        },
        get expiresAtMs() {
            return 0;
        },
        get epoch() {
            return epoch;
        },
        isExpired() {
            return false;
        },
        async load() {
            return payload;
        },
        refresh() {
            return Promise.resolve(payload);
        },
        invalidate() {
            epoch += 1;
        },
    };
}

/**
 * @param {Object} opts
 * @param {number} opts.lessonId
 * @param {{get: Function}} opts.http           - axios-like instance ($http)
 * @param {string} [opts.baseUrl]               - BFF origin (VUE_APP_VIDEO_PLAY_BFF)
 * @param {() => number} [opts.now]             - injectable clock (tests)
 */
export function createPlaybackLink({ lessonId, http, baseUrl = "", now = () => Date.now() }) {
    let payload = null;
    let expiresAtMs = 0;
    let epoch = 0; // invalidation token: bumped by invalidate() (destroy / fatal error)
    let inFlight = null; // single-flight latch: overlapping refreshes coalesce

    const url = `${baseUrl}/api/video/play?lessonId=${lessonId}`;

    async function request() {
        const response = await http.get(url);
        return response && response.data;
    }

    function adopt(data) {
        payload = data || null;
        expiresAtMs = payload && payload.expiresAt ? new Date(payload.expiresAt).getTime() : 0;
        return payload;
    }

    return {
        get url() {
            return url;
        },
        get payload() {
            return payload;
        },
        get expiresAtMs() {
            return expiresAtMs;
        },
        get epoch() {
            return epoch;
        },

        // True once the link is within EXPIRY_MARGIN_MS of expiry (or already
        // past it). A link with no expiresAt never reports expired.
        isExpired(nowMs = now()) {
            return expiresAtMs > 0 && nowMs > expiresAtMs - EXPIRY_MARGIN_MS;
        },

        // Initial load — no single-flight, no epoch guard (nothing to coalesce
        // with yet). Rejects on failure so the caller can show the error state.
        async load() {
            return adopt(await request());
        },

        // Proactive / recovery refresh. Overlapping triggers (play + seeking +
        // media error) coalesce into the one in-flight request. Resolves to the
        // fresh payload, or `null` when the request was invalidated
        // (invalidate() / destroy) before it settled — the caller must NOT then
        // touch the media element.
        refresh() {
            if (inFlight) return inFlight;
            const startEpoch = epoch;
            inFlight = request()
                .then((data) => {
                    inFlight = null;
                    if (startEpoch !== epoch) return null;
                    return adopt(data);
                })
                .catch((err) => {
                    inFlight = null;
                    if (startEpoch !== epoch) return null;
                    throw err;
                });
            return inFlight;
        },

        // Bumps the epoch so any in-flight refresh resolves to null instead of
        // being adopted. Called on component destroy and on the give-up path.
        invalidate() {
            epoch += 1;
            inFlight = null;
        },
    };
}
